import type { FieldInputProps, FieldProps } from 'formik';
import { Field, useFormikContext } from 'formik';
import React, { useEffect } from 'react';

import TextInputBox from '@components/TextInputBox';

import { IS_INPUT_SUPPORTED } from '@utils/browser/isInputSupported';
import type { CompatibleInputEvent } from '@utils/browser/normalizeInputEvent';
import { normalizeInputEvent } from '@utils/browser/normalizeInputEvent';

import { FormikCodeFieldStyle } from './Fields.style';

interface FormikCodeFieldProps {
  /**
   * Name of the field
   */
  name: string;
  /**
   * Length of the code
   */
  codeLength: number;
  /**
   * Should the form be auto submitted when the code field is complete?
   * @default true
   */
  autoSubmit?: boolean;
}

const ValueChangeListener = ({
  name,
  codeLength,
  autoSubmit = true,
}: {
  name: string;
  codeLength: number;
  autoSubmit?: boolean;
}) => {
  const { submitForm, isSubmitting, values } =
    useFormikContext<Record<string, unknown>>();
  const value = values[name] as string[];
  useEffect(() => {
    if (!isSubmitting && value.length === codeLength && autoSubmit) {
      if (value.every((x) => Number.isInteger(parseInt(x, 10)))) {
        submitForm();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return null;
};

const AutoFillListener = ({
  onAutoFill,
}: {
  onAutoFill: React.ChangeEventHandler<HTMLInputElement>;
}) => {
  const handleAutoFill = (e: React.ChangeEvent<HTMLInputElement>) => {
    onAutoFill(e);
  };

  return (
    <input
      autoComplete="one-time-code"
      id="mfaCode"
      name="mfaCode"
      type="text"
      onChange={handleAutoFill}
      style={{ opacity: 0, position: 'absolute' }}
    />
  );
};

const FormikCodeField: React.FC<FormikCodeFieldProps> = ({
  name,
  codeLength,
  autoSubmit,
}) => {
  const formik = useFormikContext();
  const itemsRef = React.useRef<HTMLInputElement[]>([]);

  const setCode = (value: string) => {
    const arr = value.split('');

    if (arr.length !== codeLength) {
      return;
    }

    const valid = arr.every((x) => Number.isInteger(parseInt(x, 10)));

    if (valid) {
      formik.setFieldValue(name, arr, false);
      itemsRef.current[codeLength - 1].focus();
    }
  };

  const handleEvent = (
    event: CompatibleInputEvent,
    field: FieldInputProps<string[]>,
    i: number
  ) => {
    const fieldName = `${name}[${i}]`;
    if (event.key && Number.isInteger(parseInt(event.key, 10))) {
      if (event.key.length === codeLength) {
        formik.setFieldValue(name, event.key.split(''), false);
        itemsRef.current[codeLength - 1].focus();
      } else {
        formik.setFieldValue(fieldName, event.key, false);

        if (i < codeLength - 1) {
          itemsRef.current[i + 1].focus();
        }
      }
    }
    if (event.inputType === 'deleteContentBackward') {
      formik.setFieldValue(fieldName, '', false);
      if (field.value[i] === '' && i > 0) {
        itemsRef.current[i - 1].focus();
      }
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    field: FieldInputProps<string[]>,
    i: number
  ) => {
    const e = normalizeInputEvent(event);
    if (!IS_INPUT_SUPPORTED || event.key.length > 1) {
      handleEvent(e, field, i);
    }
  };

  const handleInput = (
    event: React.FormEvent<HTMLDivElement>,
    field: FieldInputProps<string[]>,
    i: number
  ) => {
    const e = normalizeInputEvent(event);
    if (IS_INPUT_SUPPORTED) {
      handleEvent(e, field, i);
    }
  };

  const handlePaste: React.ClipboardEventHandler = (e) => {
    const data = e.clipboardData.getData('text');
    setCode(data);
  };

  const handleAutoFill: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const { value } = e.target;
    setCode(value);
  };

  return (
    <Field name={name}>
      {({ field }: FieldProps<string[]>) => (
        <>
          <ValueChangeListener
            autoSubmit={autoSubmit}
            codeLength={codeLength}
            name={name}
          />
          <AutoFillListener onAutoFill={handleAutoFill} />
          <div
            style={{
              columnGap: '16px',
              display: 'grid',
              gridTemplateColumns: `repeat(${codeLength}, 1fr)`,
              paddingTop: 1,
              paddingBottom: 1,
            }}
          >
            {[...Array(codeLength).keys()].map((_ref, i) => (
              <FormikCodeFieldStyle key={`code-${i}`}>
                <TextInputBox
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus={i === 0}
                  autoComplete="off"
                  name={`${name}[${i}]`}
                  onBlur={(e) => field.onBlur(e)}
                  onInput={(e) => handleInput(e, field, i)}
                  onKeyDown={(e) => handleKeyDown(e, field, i)}
                  onPaste={handlePaste}
                  inputRef={(el: HTMLInputElement) => {
                    itemsRef.current[i] = el;
                  }}
                  InputProps={{
                    sx: { minWidth: 0 },
                  }}
                  inputProps={{
                    style: {
                      display: 'inline-block',
                      textAlign: 'center',
                    },
                  }}
                  type="text"
                  inputMode="numeric"
                  value={field.value[i]}
                />
              </FormikCodeFieldStyle>
            ))}
          </div>
        </>
      )}
    </Field>
  );
};

export default FormikCodeField;
